import { extend, media, stylesheet } from 'typestyle';
import { Gray20, Gray30, White } from '@traceair/tds';
import { MissionWindowShtorka } from './MissionWindowShtorka';
import { useAppStore } from '../../../../store/appStore';
import { MissionWindowNav } from './MissionWindowNav';
import { MissionWindowPrimaryInfo } from './projectInfo/MissionWindowPrimaryInfo';
import { useCloseMissionWindowOnEsc } from './useCloseMissionWindowOnEsc';
import { useUiStore } from '../../../../store/uiStore';
import { MissionWindowUpload } from './MissionWindowUpload';
import { MissionWindowProjectInfo } from './MissionWindowProjectInfo';
import { MissionWindowTab } from '../../../../consts/missionWindowTab';
import { BREAKPOINT_SMALL } from '../../../layout/styles';
import { useLoadGcpRequest } from './projectInfo/useLoadGcpRequest';
import { useHasAccessToProjectUpload } from '../useHasAccessToProjectUpload';
import { useLoadMissionUploads } from './useLoadMissionUploads';
import { useLoadFlightMarkersLastModified } from './projectInfo/useLoadFlightMarkersLastModified';
import { useUploadStore } from '../../../../store/uploadStore';
import { GcpRequestSubmit } from './gcpRequestUpload/gcpRequestSubmit/GcpRequestSubmit';
import { useRef } from 'react';
import { GcpLeaveModal } from './GcpLeaveModal';

const css = stylesheet({
  container: {
    position: 'fixed',
    width: 600,
    maxWidth: '100%',
    right: 0,
    top: 0,
    height: '100%',
    overflow: 'auto',
    background: White,
    zIndex: 2,
    boxShadow: '-4px 0px 26px 0px rgba(0, 0, 0, 0.12)',
    borderLeft: `1px solid ${Gray30}`,
    display: 'flex',
    flexDirection: 'column',
  },
  containerInner: {
    overflow: 'auto',
    scrollbarGutter: 'stable',
  },
  header: {
    padding: '16px 24px 0',
    borderBottom: `1px solid ${Gray20}`,
  },
  tabContent: extend(
    {
      padding: 24,
      paddingBottom: 12,
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        padding: '24px 16px',
      },
    ),
  ),
});

export const MissionWindow = () => {
  const { selectedMission } = useAppStore();
  const { selectedMissionWindowTab } = useUiStore();
  const { hasGcpFormChanges, getGcpRequest } = useUploadStore();

  const canAccessProjectScanUpload = useHasAccessToProjectUpload();
  const innerRef = useRef<HTMLDivElement>(null);

  useCloseMissionWindowOnEsc();
  useLoadGcpRequest();
  useLoadFlightMarkersLastModified();
  useLoadMissionUploads(selectedMission);

  if (!selectedMission) return null;

  const { schedule, gcpPlacementService, customer, site } = selectedMission;
  const gcpRequest = getGcpRequest(customer.id, site.id);
  const hasChanges = hasGcpFormChanges(customer.id, site.id);
  const scheduleOrGcp = schedule ?? gcpPlacementService;
  const isGcpsSubmitVisible =
    gcpRequest &&
    selectedMissionWindowTab === MissionWindowTab.Upload &&
    (hasChanges || (gcpRequest.meta.isEditMode && gcpRequest.meta.prevMeasurements.length > 0));

  return (
    <div className={css.container} data-intercom-target="MissionWindow">
      <MissionWindowShtorka />

      <section className={css.containerInner} ref={innerRef}>
        <header className={css.header}>
          <MissionWindowPrimaryInfo />
          {(canAccessProjectScanUpload || scheduleOrGcp) && <MissionWindowNav />}
        </header>

        <div className={css.tabContent}>
          {selectedMissionWindowTab === MissionWindowTab.Upload ? (
            <MissionWindowUpload />
          ) : (
            <MissionWindowProjectInfo />
          )}
        </div>
      </section>

      {isGcpsSubmitVisible && (
        <GcpRequestSubmit customer={customer} site={site} gcpRequest={gcpRequest} schedule={schedule} />
      )}

      <GcpLeaveModal customer={customer} site={site} gcpRequest={gcpRequest} innerRef={innerRef} />
    </div>
  );
};
